const BASE_URL = import.meta.env.VITE_APP_API_BASE_URL as string

/**
 * Thrown on any non-2xx response. Shaped like an axios error
 * (`e.response.data`) so components can use `apiErrorMessage()` below to
 * pull the server's validation/error message out of it.
 */
export class ApiError extends Error {
  response: { status: number; data: unknown }

  constructor(status: number, data: unknown) {
    super(`Request failed with status code ${status}`)
    this.name = 'ApiError'
    this.response = { status, data }
  }
}

/** Extracts the server's `message` from a thrown `ApiError`, or `fallback`. */
export function apiErrorMessage(e: unknown, fallback: string): string {
  const data = e instanceof ApiError ? e.response.data : null
  const message = data && typeof data === 'object' ? (data as Record<string, unknown>).message : null
  return typeof message === 'string' ? message : fallback
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

let csrfReady: Promise<void> | null = null

/**
 * Sanctum's SPA cookie handshake: fetch `/sanctum/csrf-cookie` once so the
 * browser has an `XSRF-TOKEN` cookie, then echo that value back as the
 * `X-XSRF-TOKEN` header on every request (Laravel decrypts it server-side).
 */
function ensureCsrfCookie(): Promise<void> {
  if (!csrfReady) {
    csrfReady = fetch(`${BASE_URL}/sanctum/csrf-cookie`, { credentials: 'include' }).then(
      () => undefined,
    )
  }
  return csrfReady
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  if (method !== 'GET') {
    await ensureCsrfCookie()
  }

  // FormData sets its own multipart boundary in the Content-Type header —
  // letting the browser manage it (by omitting ours) is required for file
  // uploads to be parsed correctly server-side.
  const isFormData = body instanceof FormData

  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    Accept: 'application/json',
    ...extraHeaders,
  }

  const xsrfToken = readCookie('XSRF-TOKEN')
  if (xsrfToken) {
    headers['X-XSRF-TOKEN'] = xsrfToken
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    credentials: 'include',
    headers,
    body: isFormData ? body : body ? JSON.stringify(body) : undefined,
  })

  const isJson = res.headers.get('content-type')?.includes('application/json')
  const data = res.status === 204 ? null : isJson ? await res.json() : await res.text()

  if (!res.ok) {
    throw new ApiError(res.status, data)
  }

  return data as T
}

export const api = {
  get: <T>(path: string, headers?: Record<string, string>) =>
    request<T>('GET', path, undefined, headers),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>('POST', path, body, headers),
  put: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>('PUT', path, body, headers),
  patch: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>('PATCH', path, body, headers),
  delete: <T>(path: string, headers?: Record<string, string>) =>
    request<T>('DELETE', path, undefined, headers),
}